import React from "react";
import { Download } from "lucide-react";

const ENDPOINTS = [
  {
    url: "/api/voice/webhook", method: "POST", module: "voice",
    purpose: "Receives all ElevenLabs Conversational AI interactions. Logs them and triggers cross-module automations.",
    elevenlabs: { tool: "dnya_voice_webhook", location: "ElevenLabs → Agents → Dnya Assistant → Tools → Webhook" },
    request: { user_id: "uuid", module: "calendar|messaging|geolocation|hajj|murshid", user_message: "string", agent_response: "string", session_id: "string" },
    response: { status: "ok", module: "string", action_taken: "boolean" },
    table: "(future) voice_logs", auth: "No (validated via ElevenLabs signature)",
    test: ['POST a sample payload via curl. Expect 200 with {"status":"ok"}.'],
  },
  {
    url: "/api/voice/intent", method: "POST", module: "voice",
    purpose: "Classifies a user text/voice command into module + action + extracted fields. Powered by Claude Sonnet 4.5.",
    elevenlabs: { tool: "dnya_intent", location: "ElevenLabs → Agents → Dnya Assistant → Tools" },
    request: { user_input: "string", current_module: "string" },
    response: { module: "string", action: "string", confidence: "0-1", extracted_fields: "object", missing_fields: "array", spoken_response: "string" },
    table: "—", auth: "Bearer JWT",
    test: ['Say: "find the nearest mosque" → expect module=geolocation, action=find_nearby'],
  },
  {
    url: "/api/voice/signed-url", method: "GET", module: "voice",
    purpose: "Returns ElevenLabs signed URL + per-user dynamic_variables (name, madhab, language) to inject into agent.",
    elevenlabs: { tool: "—", location: "Used internally by frontend before startSession" },
    request: {}, response: { signed_url: "string|null", agent_id: "string", dynamic_variables: "object" },
    table: "profiles (reads madhab, full_name, preferred_language)", auth: "Bearer JWT",
    test: ["Login, GET /api/voice/signed-url with bearer token, expect dynamic_variables.user_name to match profile."],
  },
  {
    url: "/api/calendar/events", method: "GET", module: "calendar", purpose: "List user's calendar events.",
    elevenlabs: { tool: "list_events", location: "ElevenLabs → Tools" },
    request: { start: "ISO?", end: "ISO?" }, response: { events: "array" },
    table: "calendar_events (read)", auth: "Bearer JWT",
    test: ['Voice: "show my schedule" → returns events JSON'],
  },
  {
    url: "/api/calendar/events", method: "POST", module: "calendar", purpose: "Create a new calendar event.",
    elevenlabs: { tool: "create_event", location: "ElevenLabs → Tools" },
    request: { title: "string", start_at: "ISO", event_type: "general|prayer|zakat|hajj|reminder", description: "string?", end_at: "ISO?", hijri_date: "string?", is_recurring: "bool?", recurrence_rule: "string?" },
    response: { id: "uuid", "...event row": "" },
    table: "calendar_events (insert)", auth: "Bearer JWT",
    test: ['Voice: "schedule meeting tomorrow 2pm". Check Supabase calendar_events row exists.'],
  },
  {
    url: "/api/calendar/events/{id}", method: "PUT", module: "calendar", purpose: "Update an existing event.",
    elevenlabs: { tool: "update_event", location: "ElevenLabs → Tools" },
    request: { title: "string?", start_at: "ISO?", "...other fields": "" }, response: { id: "uuid", "...updated row": "" },
    table: "calendar_events (update)", auth: "Bearer JWT", test: ["PUT with an event id and new title. Check DB."],
  },
  {
    url: "/api/calendar/events/{id}", method: "DELETE", module: "calendar", purpose: "Delete an event.",
    elevenlabs: { tool: "delete_event", location: "ElevenLabs → Tools" },
    request: {}, response: { ok: true }, table: "calendar_events (delete)", auth: "Bearer JWT",
    test: ["Confirm row removed from Supabase."],
  },
  {
    url: "/api/murshid/ask", method: "POST", module: "murshid", purpose: "Ask Murshid a question. Returns assistant answer + saves to guidance_sessions.",
    elevenlabs: { tool: "ask_murshid", location: "ElevenLabs → Tools" },
    request: { question: "string", session_id: "uuid?", topic: "string?" },
    response: { session_id: "uuid", answer: "string", messages: "array" },
    table: "guidance_sessions (insert/update)", auth: "Bearer JWT",
    test: ['Voice: "is music halal" → expect text answer + guidance_sessions row.'],
  },
  {
    url: "/api/murshid/sessions", method: "GET", module: "murshid", purpose: "List user's guidance sessions.",
    elevenlabs: { tool: "list_guidance", location: "ElevenLabs → Tools" },
    request: {}, response: { sessions: "array" }, table: "guidance_sessions (read)", auth: "Bearer JWT",
    test: ["GET with bearer token."],
  },
  {
    url: "/api/prayer/times", method: "GET", module: "calendar", purpose: "Fetches today's 5 daily prayer times (Aladhan API, madhab-aware).",
    elevenlabs: { tool: "prayer_times", location: "ElevenLabs → Tools" },
    request: { city: "string", country: "string" },
    response: { city: "string", country: "string", gregorian: "DD-MM-YYYY", hijri: "string", timings: { Fajr: "HH:MM", Dhuhr: "HH:MM", Asr: "HH:MM", Maghrib: "HH:MM", Isha: "HH:MM" } },
    table: "—", auth: "Bearer JWT",
    test: ['Voice: "what time is fajr today" → returns timings.'],
  },
  {
    url: "/api/auth/signup", method: "POST", module: "auth", purpose: "Email/password signup. Creates Supabase auth user + profile.",
    elevenlabs: { tool: "—", location: "—" },
    request: { email: "string", password: "string", full_name: "string", terms_accepted: "true", madhab: "string?", preferred_language: "string?" },
    response: { token: "JWT", user: "profile" },
    table: "auth.users + profiles (insert)", auth: "Public",
    test: ["POST. Verify Supabase auth.users row + profile row."],
  },
  {
    url: "/api/auth/login", method: "POST", module: "auth", purpose: "Email/password login.",
    elevenlabs: { tool: "—", location: "—" },
    request: { email: "string", password: "string" }, response: { token: "JWT", user: "profile" },
    table: "auth.users + profiles (read)", auth: "Public",
    test: ["POST with valid creds → 200. Wrong password → 401."],
  },
  {
    url: "/api/auth/google/session", method: "POST", module: "auth", purpose: "Exchange Emergent Google session_id for a Dnya JWT.",
    elevenlabs: { tool: "—", location: "—" },
    request: { session_id: "string" }, response: { token: "JWT", user: "profile" },
    table: "auth.users + profiles (upsert)", auth: "Public",
    test: ["Hit Emergent Google flow, observe AuthCallback exchanges fragment for JWT."],
  },
];

export default function WebhookReport() {
  const download = () => {
    const blob = new Blob([JSON.stringify(ENDPOINTS, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "dnya-webhook-report.json";
    a.click();
  };

  return (
    <div className="dnya-reveal">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <div>
          <h1 className="font-dnya" style={{ fontSize: 28, color: "var(--dnya-high-1)", margin: 0 }}>Webhook Configuration Report</h1>
          <div style={{ color: "var(--dnya-high-2)", fontSize: 13, marginTop: 4 }}>All Dnya API endpoints, ElevenLabs setup and test procedures</div>
        </div>
        <button onClick={download} style={{ padding: "10px 18px", borderRadius: 10, background: "var(--dnya-mosaic-1)", color: "#fff", border: 0, cursor: "pointer", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Download size={16} /> Download JSON
        </button>
      </div>

      {ENDPOINTS.map((e) => (
        <div key={`${e.method}-${e.url}`} style={{ background: "var(--dnya-card)", border: `1px solid var(--dnya-mosaic-1)`, borderRadius: 12, padding: 18, marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
            <span style={{ background: "var(--dnya-mosaic-1)", color: "#fff", padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: "0.1em" }}>{e.method}</span>
            <code style={{ color: "var(--dnya-high-1)", fontSize: 14, fontWeight: 600 }}>{e.url}</code>
            <span style={{ marginLeft: "auto", color: "var(--dnya-high-2)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em" }}>{e.module}</span>
          </div>
          <p style={{ color: "var(--dnya-text-muted)", fontSize: 13, lineHeight: 1.55, margin: "6px 0 12px" }}>{e.purpose}</p>
          <Section title="ElevenLabs configuration" v={`Tool name: ${e.elevenlabs.tool} · Add at: ${e.elevenlabs.location}`} />
          <Section title="Request payload" v={<pre style={pre}>{JSON.stringify(e.request, null, 2)}</pre>} />
          <Section title="Response structure" v={<pre style={pre}>{JSON.stringify(e.response, null, 2)}</pre>} />
          <Section title="Supabase table" v={e.table} />
          <Section title="Authentication" v={e.auth} />
          <Section title="Test procedure" v={<ul style={{ paddingLeft: 18, color: "var(--dnya-text-muted)", fontSize: 12, margin: 0 }}>{e.test.map((t) => <li key={t} style={{ marginBottom: 4 }}>{t}</li>)}</ul>} />
        </div>
      ))}
    </div>
  );
}

const Section = ({ title, v }) => (
  <div style={{ marginBottom: 8 }}>
    <div style={{ fontSize: 11, color: "var(--dnya-high-1)", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>{title}</div>
    {typeof v === "string" ? <div style={{ color: "var(--dnya-text-muted)", fontSize: 13 }}>{v}</div> : v}
  </div>
);
const pre = { background: "var(--dnya-bg)", color: "var(--dnya-high-2)", padding: 10, borderRadius: 8, fontSize: 12, margin: 0, overflowX: "auto" };
